import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, User, Calendar, MapPin, Shield, Award } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountInfoPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      setProfile(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !profile) {
    return <div style={{ padding: '20px', color: '#E6D7C3' }}>Loading...</div>;
  }

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/profile')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D7B56D', padding: '8px' }}>
          <ArrowLeft size={24} />
        </button>
        <h1 className="page-title" style={{ fontFamily: 'Playfair Display, serif' }}>Account Information</h1>
      </div>

      <div style={{ padding: '20px', paddingBottom: '100px' }}>
        {/* Basic Information Card */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#D7B56D', marginBottom: '20px', fontFamily: 'Playfair Display, serif' }}>
            Basic Information
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <User size={20} style={{ color: '#D7B56D' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#A57C33', marginBottom: '2px' }}>Full Name</div>
                <div style={{ color: '#E6D7C3', fontWeight: '500' }}>{profile.name}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <Mail size={20} style={{ color: '#D7B56D' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#A57C33', marginBottom: '2px' }}>Email Address</div>
                <div style={{ color: '#E6D7C3', fontWeight: '500' }}>{profile.email || 'alex.rivera@example.com'}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <User size={20} style={{ color: '#D7B56D' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#A57C33', marginBottom: '2px' }}>Username</div>
                <div style={{ color: '#E6D7C3', fontWeight: '500' }}>@{profile.name.toLowerCase().replace(' ', '_')}</div>
              </div>
            </div>

            {profile.age && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
                <Calendar size={20} style={{ color: '#D7B56D' }} />
                <div>
                  <div style={{ fontSize: '12px', color: '#A57C33', marginBottom: '2px' }}>Age</div>
                  <div style={{ color: '#E6D7C3', fontWeight: '500' }}>{profile.age} years old</div>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <MapPin size={20} style={{ color: '#D7B56D' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#A57C33', marginBottom: '2px' }}>Location</div>
                <div style={{ color: '#E6D7C3', fontWeight: '500' }}>{profile.location}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <Shield size={20} style={{ color: '#D7B56D' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#A57C33', marginBottom: '2px' }}>Account Type</div>
                <div style={{ color: '#D7B56D', fontWeight: '600' }}>{profile.elite ? 'Elite Member' : 'Standard Member'}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', background: 'rgba(230, 215, 195, 0.05)', borderRadius: '8px', border: '1px solid rgba(165, 124, 51, 0.3)' }}>
              <Calendar size={20} style={{ color: '#D7B56D' }} />
              <div>
                <div style={{ fontSize: '12px', color: '#A57C33', marginBottom: '2px' }}>Member Since</div>
                <div style={{ color: '#E6D7C3', fontWeight: '500' }}>January 2024</div>
              </div>
            </div>
          </div>
        </div>

        {/* Account Status */}
        <div className="card">
          <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#D7B56D', marginBottom: '16px', fontFamily: 'Playfair Display, serif' }}>
            Verification Status
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'rgba(40, 167, 69, 0.1)', borderRadius: '8px', border: '1px solid #28a745' }}>
              <span style={{ color: '#E6D7C3', fontSize: '14px' }}>Email Verified</span>
              <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: '14px' }}>✓ Verified</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px', background: 'rgba(40, 167, 69, 0.1)', borderRadius: '8px', border: '1px solid #28a745' }}>
              <span style={{ color: '#E6D7C3', fontSize: '14px' }}>ID Verified</span>
              <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: '14px' }}>✓ Verified</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AccountInfoPage;